import { ClientFormSection } from '@src/clients/ClientFormSection';
import type { Client } from '@src/clients/domain/Client';
import { useUpdateDefaultBillTo } from '@src/clients/financialSettings/hooks/useUpdateDefaultBillTo';
import { Radio, RadioGroup } from '@src/common/components/Radio';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { BillToDropdown } from '@src/orders/components/billTo/BillToDropdown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  client: Client;
}

export const ThirdPartyBillingSection = ({ client }: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [hasBillTo, setHasDefaultBillTo] = useState(
    !!client.defaultBillToClientId,
  );
  const { updateDefaultBillTo } = useUpdateDefaultBillTo({
    clientId: client.id,
    onSuccess: () => {
      showToast({
        severity: ToastLevel.success,
        message: t('thirdPartyBillingSection.success'),
      });
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('thirdPartyBillingSection.error'),
      }),
  });

  const onHasDefaultBillToChange = (value: boolean) => {
    setHasDefaultBillTo(value);
    if (!value) {
      updateDefaultBillTo({ defaultBillToClientId: undefined });
    }
  };

  return (
    <ClientFormSection title={t('thirdPartyBilling')}>
      <Typography component="h4" variant="body1" style={{ fontWeight: 500 }}>
        {t('thirdPartyBillingDescription')}
      </Typography>
      <RadioGroup
        onChange={(_, value) => onHasDefaultBillToChange(value === 'true')}
        value={hasBillTo}
      >
        {[
          { label: t('yes'), value: true },
          { label: t('no'), value: false },
        ].map((option) => (
          <Radio
            key={`${option.value}`}
            value={option.value}
            label={t(`${option.label}`)}
          />
        ))}
      </RadioGroup>
      {hasBillTo && (
        <BillToDropdown
          clientId={client.defaultBillToClientId}
          onChange={(client) =>
            updateDefaultBillTo({ defaultBillToClientId: client.id })
          }
        />
      )}
    </ClientFormSection>
  );
};
